/**
 * ExpedienteEditDialog — full-field edit of an expediente. Backed by
 * react-hook-form + zodResolver against the widened `updateExpedienteSchema`
 * (identity, classification, risk and scheduling fields). Prefilled from the
 * current expediente; only the fields the user actually changed (dirty) are sent
 * in the PATCH, so untouched values are never clobbered.
 *
 * Note: `assignedTo` is intentionally not edited here — reassignment is a
 * separate user-picker flow, and the UI expediente carries the assignee's name,
 * not the uuid the schema requires.
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateExpedienteSchema, type UpdateExpedienteInput } from '@cumpli/shared';
import { api } from '../lib/api';
import { invalidateExpedienteTimeline } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { Button, Dialog, FieldError, Input, Label, cn } from './ui';
import { RIESGO_LABEL, type Expediente } from '../lib/phase1';

const selectCn =
  'flex h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50';

const ESTADOS = [
  'Nuevo',
  'En Revisión',
  'Docs Pendientes',
  'Escalado',
  'Aprobado',
  'Activo',
  'Rechazado',
  'Actualización Requerida',
] as const;
const RIESGOS = ['bajo', 'medio', 'alto', 'sin'] as const;
const DOC_KINDS: { v: string; label: string }[] = [
  { v: 'cedula', label: 'Cédula' },
  { v: 'ruc', label: 'RUC' },
  { v: 'pasaporte', label: 'Pasaporte' },
  { v: 'folio', label: 'Folio' },
  { v: 'otro', label: 'Otro' },
];
const CANALES: { v: string; label: string }[] = [
  { v: 'presencial', label: 'Presencial' },
  { v: 'no_presencial', label: 'No presencial' },
  { v: 'mixto', label: 'Mixto' },
  { v: 'corresponsal', label: 'Corresponsal' },
];
const PRODUCTOS: { v: string; label: string }[] = [
  { v: 'cuenta_basica', label: 'Cuenta básica' },
  { v: 'ahorro', label: 'Ahorro' },
  { v: 'credito', label: 'Crédito' },
  { v: 'inversion', label: 'Inversión' },
  { v: 'efectivo', label: 'Efectivo' },
  { v: 'criptoactivos', label: 'Criptoactivos' },
  { v: 'comercio_exterior', label: 'Comercio exterior' },
];

// Coercers: empty DOM value -> null (nullable fields) or undefined (required-ish).
const emptyToNull = (v: unknown) => (v === '' || v === undefined ? null : v);
const emptyToUndef = (v: unknown) => (v === '' || v === undefined ? undefined : v);
const day = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : null);

export function ExpedienteEditDialog({ exp, onClose }: { exp: Expediente; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<UpdateExpedienteInput>({
    resolver: zodResolver(updateExpedienteSchema),
    defaultValues: {
      nombre: exp.name,
      persona: exp.persona,
      docId: exp.docId,
      docKind: exp.docKind ?? null,
      folio: exp.folio ?? null,
      sector: exp.sector,
      kind: exp.kind,
      email: exp.email,
      phone: exp.phone,
      dateOfBirth: day(exp.dateOfBirth),
      direccion: exp.direccion ?? null,
      constitutionDate: day(exp.constitutionDate),
      estado: exp.estado,
      riesgo: exp.riesgo,
      score: exp.score,
      pep: exp.pep,
      canal: exp.canal ?? null,
      producto: exp.producto ?? null,
      nextReviewAt: day(exp.review),
    },
  });

  const save = useMutation({
    mutationFn: (patch: UpdateExpedienteInput) => api.updateExpediente(exp.id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['expedientes'] });
      await qc.invalidateQueries({ queryKey: ['expedientes', exp.id] });
      // Bug fix: an expediente edit must show up in Actividad live too.
      await invalidateExpedienteTimeline(qc, exp.id);
      toast({ message: 'Expediente actualizado', kind: 'success' });
      onClose();
    },
    onError: (err) => {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo guardar el expediente',
        kind: 'error',
      });
    },
  });

  const onSubmit = (data: UpdateExpedienteInput) => {
    // Send only what changed, so untouched fields (incl. ones the UI can't
    // prefill) are never overwritten.
    const patch: Record<string, unknown> = {};
    for (const key of Object.keys(dirtyFields) as (keyof UpdateExpedienteInput)[]) {
      patch[key] = data[key];
    }
    if (Object.keys(patch).length === 0) {
      toast({ message: 'No hay cambios por guardar', kind: 'error' });
      return;
    }
    save.mutate(patch as UpdateExpedienteInput);
  };

  return (
    <Dialog open size="lg" title="Editar expediente" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="ee-nombre">Nombre</Label>
            <Input id="ee-nombre" {...register('nombre', { setValueAs: emptyToUndef })} />
            <FieldError message={errors.nombre && 'Ingresa un nombre válido (mínimo 2 caracteres)'} />
          </div>

          <div>
            <Label htmlFor="ee-persona">Tipo de persona</Label>
            <select id="ee-persona" className={selectCn} {...register('persona')}>
              <option value="natural">Persona natural</option>
              <option value="legal">Persona jurídica</option>
            </select>
          </div>
          <div>
            <Label htmlFor="ee-docKind">Tipo de identificación</Label>
            <select id="ee-docKind" className={selectCn} {...register('docKind', { setValueAs: emptyToNull })}>
              <option value="">—</option>
              {DOC_KINDS.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="ee-docId">Identificación (cédula / RUC)</Label>
            <Input id="ee-docId" {...register('docId', { setValueAs: emptyToUndef })} />
            <FieldError message={errors.docId && 'Identificación requerida'} />
          </div>
          <div>
            <Label htmlFor="ee-folio">Folio (Registro Público)</Label>
            <Input id="ee-folio" {...register('folio', { setValueAs: emptyToNull })} />
          </div>

          <div>
            <Label htmlFor="ee-sector">Sector / actividad</Label>
            <Input id="ee-sector" {...register('sector', { setValueAs: emptyToUndef })} />
            <FieldError message={errors.sector && 'Sector requerido'} />
          </div>
          <div>
            <Label htmlFor="ee-kind">Subtipo</Label>
            <Input id="ee-kind" {...register('kind', { setValueAs: emptyToUndef })} />
            <FieldError message={errors.kind && 'Subtipo requerido'} />
          </div>

          <div>
            <Label htmlFor="ee-email">Correo electrónico</Label>
            <Input id="ee-email" type="email" {...register('email', { setValueAs: emptyToNull })} />
            <FieldError message={errors.email && 'Correo inválido'} />
          </div>
          <div>
            <Label htmlFor="ee-phone">Teléfono</Label>
            <Input id="ee-phone" {...register('phone', { setValueAs: emptyToNull })} />
          </div>

          <div>
            <Label htmlFor="ee-dob">Fecha de nacimiento</Label>
            <Input id="ee-dob" type="date" {...register('dateOfBirth', { setValueAs: emptyToNull })} />
          </div>
          <div>
            <Label htmlFor="ee-const">Fecha de constitución</Label>
            <Input id="ee-const" type="date" {...register('constitutionDate', { setValueAs: emptyToNull })} />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="ee-direccion">Domicilio</Label>
            <Input id="ee-direccion" {...register('direccion', { setValueAs: emptyToNull })} />
          </div>

          <div>
            <Label htmlFor="ee-estado">Estado</Label>
            <select id="ee-estado" className={selectCn} {...register('estado')}>
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ee-riesgo">Riesgo</Label>
            <select id="ee-riesgo" className={selectCn} {...register('riesgo')}>
              {RIESGOS.map((r) => (
                <option key={r} value={r}>
                  {RIESGO_LABEL[r]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="ee-score">Puntaje de riesgo (0–100)</Label>
            <Input
              id="ee-score"
              type="number"
              min={0}
              max={100}
              {...register('score', {
                setValueAs: (v) => (v === '' || v === null || v === undefined ? undefined : Number(v)),
              })}
            />
            <FieldError message={errors.score && 'Puntaje entre 0 y 100'} />
          </div>
          <div>
            <Label htmlFor="ee-review">Próxima revisión</Label>
            <Input id="ee-review" type="date" {...register('nextReviewAt', { setValueAs: emptyToNull })} />
          </div>

          <div>
            <Label htmlFor="ee-canal">Canal de vinculación</Label>
            <select id="ee-canal" className={selectCn} {...register('canal', { setValueAs: emptyToNull })}>
              <option value="">—</option>
              {CANALES.map((c) => (
                <option key={c.v} value={c.v}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ee-producto">Producto / servicio</Label>
            <select id="ee-producto" className={selectCn} {...register('producto', { setValueAs: emptyToNull })}>
              <option value="">—</option>
              {PRODUCTOS.map((p) => (
                <option key={p.v} value={p.v}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2.5 rounded-xl border border-line bg-panel p-3 text-sm sm:col-span-2">
            <input type="checkbox" {...register('pep')} className="h-4 w-4 shrink-0 accent-primary" />
            <span className="font-medium text-ink">Persona expuesta políticamente (PEP)</span>
          </label>
        </div>

        <div className={cn('flex flex-wrap justify-end gap-3')}>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isSubmitting || save.isPending}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
